import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles, articlesByCategory } from '@/mocks/articles';

const categoryTabs = [
  { key: 'all', label: '全部' },
  { key: 'training', label: '体育培训' },
  { key: 'events', label: '赛事组织' },
  { key: 'equipment', label: '运动装备' },
  { key: 'venues', label: '场馆管理' },
];

export default function Articles() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('category') || 'all';
  const [activeCategory, setActiveCategory] = useState(categoryParam);

  useEffect(() => {
    setActiveCategory(categoryParam);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [categoryParam]);

  const handleCategoryChange = (key: string) => {
    if (key === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ category: key });
    }
  };

  const displayArticles = articlesByCategory(activeCategory);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '体育资讯' },
  ];

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <section className="relative w-full h-[280px] md:h-[340px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Sports%20journalism%20and%20news%20concept%20with%20athletes%20in%20newspaper%20layout%2C%20warm%20lighting%2C%20editorial%20style%2C%20modern%20sports%20media%20aesthetic%2C%20warm%20orange%20and%20cream%20tones&width=1800&height=700&seq=yunkai-art-hero&orientation=landscape"
          alt="体育资讯"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/55"></div>
        <div className="relative z-10 text-center px-6">
          <h1 className="text-2xl md:text-4xl font-bold font-heading text-background-50 mb-3">体育资讯</h1>
          <p className="text-sm md:text-base text-background-50/75">最新体育动态与专业解读</p>
        </div>
      </section>

      <Breadcrumb items={breadcrumbItems} />

      <section className="w-full py-8 bg-background-50 border-b border-background-200/70">
        <div className="w-full px-6 md:px-8">
          <div className="flex items-center flex-wrap gap-2">
            {categoryTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleCategoryChange(tab.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeCategory === tab.key
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-800'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <main className="w-full py-12 md:py-16 bg-background-50">
        <div className="w-full px-6 md:px-8">
          {displayArticles.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                <i className="ri-article-line text-foreground-400 text-2xl"></i>
              </div>
              <p className="text-foreground-500">暂无相关文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/articles/${article.slug}`}
                  className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 rounded-md bg-primary-500/90 backdrop-blur-sm text-background-50 text-xs font-medium">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h2 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-500 leading-relaxed mb-4 line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i>
                          {article.publishDate}
                        </span>
                      </div>
                      <span>{article.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}